import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { formatINR } from '@/utils/format';
import { complianceApi, type TDSEntryRecord, type TDSSectionRecord, type ChallanGroupRecord, type ChallanSummary } from '@/api/compliance.api';
import DashboardTab from './components/DashboardTab';
import QuarterlyReturnPage from './components/QuarterlyReturnPage';
import Form16ACertificate from './components/Form16ACertificate';
import TdsReceivableTab from './components/TdsReceivableTab';
import ChallanDepositFlow from './components/ChallanDepositFlow';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function sectionLabel(sec: string, sections: TDSSectionRecord[]): string {
  const s = sections.find(x => x.section === sec);
  return s ? `${sec} — ${s.description}` : sec;
}

function statusBadge(status: TDSEntryRecord['status']) {
  const map: Record<string, { cls: string; label: string }> = {
    DEDUCTED: { cls: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Deducted' },
    DEPOSITED: { cls: 'bg-green-100 text-green-700 border-green-200', label: 'Deposited' },
    PENDING: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Pending' },
    EXEMPTED: { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: 'Exempted' },
  };
  const m = map[status] ?? { cls: 'bg-gray-100 text-gray-500', label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function typeBadge(type: 'TDS' | 'TCS') {
  return type === 'TDS'
    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">TDS</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-700 border border-teal-200">TCS</span>;
}

// ─── Detail Slide-Over ───────────────────────────────────────────────────────
function DetailSlideOver({ entry, sections, onClose, onForm16A }: { entry: TDSEntryRecord; sections: TDSSectionRecord[]; onClose: () => void; onForm16A: (entry: TDSEntryRecord) => void }) {
  const sectionDesc = sectionLabel(entry.section, sections);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-lg bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">TDS/TCS Details</h3>
            <p className="text-xs text-slate-400 mt-0.5">{entry.partyName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Section 1: Transaction Details */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Transaction Details</p>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Type</span>
                {typeBadge(entry.type)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Section</span>
                <span className="text-sm font-medium text-[#1e293b]">{sectionDesc}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Party</span>
                <span className="text-sm font-semibold text-[#1e293b]">{entry.partyName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">PAN</span>
                <span className="text-sm font-mono text-[#1e293b]">{entry.partyPAN}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Party Type</span>
                <span className="text-sm text-[#1e293b]">{entry.partyType}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Payment Details */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Payment Details</p>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Invoice Number</span>
                <span className="text-sm font-medium text-[#4f46e5]">{entry.invoiceNumber ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Payment Date</span>
                <span className="text-sm text-[#1e293b]">{formatDate(entry.paymentDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Payment Amount</span>
                <span className="text-sm font-semibold text-[#1e293b]">{formatINR(entry.paymentAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Taxable Amount</span>
                <span className="text-sm text-[#1e293b]">{formatINR(entry.taxableAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">TDS Rate</span>
                <span className="text-sm font-semibold text-[#1e293b]">{entry.tdsRate}%</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[#e2e8f0]">
                <span className="text-xs font-semibold text-slate-700">TDS Amount</span>
                <span className="text-sm font-bold text-red-600">{formatINR(entry.tdsAmount)}</span>
              </div>
            </div>
          </div>

          {/* Section 3: Deposit Details */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Deposit Details</p>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Status</span>
                {statusBadge(entry.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Challan Number</span>
                <span className="text-sm font-mono text-[#1e293b]">{entry.tdsChallanNumber ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Deposit Date</span>
                <span className="text-sm text-[#1e293b]">{formatDate(entry.depositDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Quarterly Return</span>
                <span className="text-sm text-[#1e293b]">{entry.quarterlyReturn ?? '—'}</span>
              </div>
              {entry.bankName && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Bank</span>
                  <span className="text-sm text-[#1e293b]">{entry.bankName}</span>
                </div>
              )}
              {entry.bankBSRCode && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">BSR Code</span>
                  <span className="text-sm font-mono text-[#1e293b]">{entry.bankBSRCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#e2e8f0] shrink-0 flex items-center justify-between">
          {entry.status === 'DEPOSITED' && (
            <button
              onClick={() => onForm16A(entry)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg border border-emerald-300 text-sm font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-file-shield-line" /> Generate Form 16A
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="h-9 px-5 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Deposit Modal ────────────────────────────────────────────────────────────
function DepositModal({ entry, onClose, onSaved }: { entry: TDSEntryRecord; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [challanNo, setChallanNo] = useState('');
  const [bsrCode, setBsrCode] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankName, setBankName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!challanNo.trim()) { toast.error('Challan number is required'); return; }
    if (!depositDate) { toast.error('Deposit date is required'); return; }
    setSaving(true);
    try {
      const res = await complianceApi.recordTDSChallanDeposit({
        challanNumber: challanNo,
        bsrCode: bsrCode || '0000000',
        bankName: bankName || 'Bank',
        depositDate,
        entryIds: [entry.id],
        totalAmount: entry.tdsAmount,
      });
      if (res?.success) {
        toast.success('TDS deposit recorded successfully');
        onSaved();
      }
    } catch (err) {
      console.error('Failed to record TDS deposit:', err);
      toast.error('Failed to record TDS deposit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">Record TDS Deposit</h3>
            <p className="text-xs text-slate-400">{entry.partyName} · {formatINR(entry.tdsAmount)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <i className="ri-information-line mr-1" />
            TDS must be deposited by the 7th of the next month.
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Challan Number <span className="text-red-500">*</span></label>
              <input type="text" value={challanNo} onChange={e => setChallanNo(e.target.value)}
                placeholder="e.g. CHLN2024041500123"
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Bank BSR Code</label>
              <input type="text" value={bsrCode} onChange={e => setBsrCode(e.target.value)}
                placeholder="7-digit BSR code"
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Deposit Date <span className="text-red-500">*</span></label>
              <input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Bank Name</label>
              <input type="text" value={bankName} onChange={e => setBankName(e.target.value)}
                placeholder="e.g. HDFC Bank"
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0]">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 h-9 px-5 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : <><i className="ri-save-line" /> Save Deposit</>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Challan Detail Modal ─────────────────────────────────────────────────────
function ChallanDetailModal({ group, onClose }: { group: ChallanGroupRecord; onClose: () => void }) {
  const [entries, setEntries] = useState<TDSEntryRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await complianceApi.getTDSEntries({});
        if (res?.success && Array.isArray(res.data)) {
          setEntries(res.data.filter((e: TDSEntryRecord) => group.entryIds.includes(e.id)));
        }
      } catch (err) {
        console.error('Failed to load challan entries:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [group.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">Challan — {group.challanNumber}</h3>
            <p className="text-xs text-slate-400">{group.quarter} {group.financialYear} · {group.bankName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-6 py-4 border-b border-[#e2e8f0] shrink-0 grid grid-cols-4 gap-4">
          {[
            { label: 'BSR Code', val: group.bsrCode }, { label: 'Deposit Date', val: formatDate(group.depositDate) },
            { label: 'Quarter', val: `${group.quarter} ${group.financialYear}` }, { label: 'Total Amount', val: formatINR(group.totalAmount) },
          ].map(({ label, val }) => (
            <div key={label}><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-semibold text-[#1e293b]">{val}</p></div>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0]">
                <th className="py-2 font-medium">Party</th><th className="py-2 font-medium">Section</th>
                <th className="py-2 font-medium text-right">TDS Amount</th><th className="py-2 font-medium text-right">Payment Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-8 text-center text-slate-400 text-sm"><i className="ri-loader-4-line animate-spin mr-1" /> Loading entries...</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={4} className="py-8 text-center text-slate-400 text-sm">No linked entries found</td></tr>
              ) : entries.map(e => (
                <tr key={e.id} className="border-b border-[#f1f5f9]">
                  <td className="py-2 text-[#1e293b]">{e.partyName}</td><td className="py-2 text-slate-500">{e.section}</td>
                  <td className="py-2 text-right font-medium text-red-600">{formatINR(e.tdsAmount)}</td>
                  <td className="py-2 text-right text-[#1e293b]">{formatINR(e.paymentAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <button onClick={() => window.print()} className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap">
            <i className="ri-printer-line" /> Print Challan
          </button>
          <button onClick={onClose} className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function TDSPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tds' | 'tcs' | 'challan' | 'receivable'>('dashboard');
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailEntry, setDetailEntry] = useState<any | null>(null);
  const [depositEntry, setDepositEntry] = useState<any | null>(null);
  const [showGenerateChallan, setShowGenerateChallan] = useState(false);
  const [challanDetail, setChallanDetail] = useState<any | null>(null);
  const [showQuarterlyReturn, setShowQuarterlyReturn] = useState(false);
  const [form16AEntry, setForm16AEntry] = useState<any | null>(null);

  // Live entries, sections, and challan groups from API — no mock fallback
  const [tdsEntriesList, setTdsEntriesList] = useState<TDSEntryRecord[]>([]);
  const [tdsSectionsList, setTdsSectionsList] = useState<TDSSectionRecord[]>([]);
  const [challanGroupsList, setChallanGroupsList] = useState<ChallanGroupRecord[]>([]);
  const [challanSummary, setChallanSummary] = useState<ChallanSummary | null>(null);
  const [challanTypeView, setChallanTypeView] = useState<'TDS' | 'TCS'>('TDS');
  const [receivableCount, setReceivableCount] = useState(0);

  // Independent of activeTab so the "TDS Receivable" tab badge stays accurate even
  // while sitting on a different tab (fetchTDSData's type filter excludes it otherwise).
  const refreshReceivableCount = () => {
    complianceApi.getTdsReceivable().then((res) => {
      if (res?.success) setReceivableCount((res.data || []).length);
    }).catch(() => {});
  };
  useEffect(() => { refreshReceivableCount(); }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'challan') return;
    let mounted = true;
    complianceApi.getChallanSummary(challanTypeView).then((res) => {
      if (mounted && res?.success) setChallanSummary(res.data || null);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [activeTab, challanTypeView, challanGroupsList]);

  const fetchTDSData = async () => {
    try {
      const [entriesRes, sectionsRes, challanRes] = await Promise.all([
        complianceApi.getTDSEntries({
          type: activeTab === 'tds' ? 'TDS' : activeTab === 'tcs' ? 'TCS' : 'ALL',
          section: sectionFilter || 'ALL',
          status: statusFilter || 'ALL',
        }),
        complianceApi.getTDSSections(),
        complianceApi.getChallanGroups(),
      ]);

      if (entriesRes?.success && Array.isArray(entriesRes.data)) {
        setTdsEntriesList(entriesRes.data);
      }
      if (sectionsRes?.success && Array.isArray(sectionsRes.data)) {
        setTdsSectionsList(sectionsRes.data);
      }
      if (challanRes?.success && Array.isArray(challanRes.data)) {
        setChallanGroupsList(challanRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch TDS data:', err);
    }
  };

  useEffect(() => {
    fetchTDSData();
  }, [activeTab, sectionFilter, statusFilter]);

  // ─── Summary calculations ────────────────────────────────────────────
  const entriesSource = tdsEntriesList;

  const tdsThisMonth = entriesSource
    .filter(e => e.type === 'TDS')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const tcsThisMonth = entriesSource
    .filter(e => e.type === 'TCS')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const pendingDeposit = entriesSource
    .filter(e => e.status === 'DEDUCTED')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const depositedQtr = entriesSource
    .filter(e => e.status === 'DEPOSITED')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  // ─── Filter entries ────────────────────────────────────────────────
  const allEntries = entriesSource.filter(e => {
    if (activeTab === 'tds' && e.type !== 'TDS') return false;
    if (activeTab === 'tcs' && e.type !== 'TCS') return false;
    if (activeTab === 'challan' || activeTab === 'dashboard') return false;
    if (search) {
      const q = search.toLowerCase();
      if (!e.partyName.toLowerCase().includes(q) && !e.partyPAN.toLowerCase().includes(q) && !(e.invoiceNumber ?? '').toLowerCase().includes(q)) return false;
    }
    if (sectionFilter && e.section !== sectionFilter) return false;
    if (statusFilter && e.status !== statusFilter) return false;
    return true;
  });

  const challanGroups = challanGroupsList
    .filter(g => (g.challanType || 'TDS') === challanTypeView)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const usedSections = [...new Set(entriesSource.map(e => e.section))];

  const handleDepositFromDashboard = (entryId: string) => {
    const entry = entriesSource.find(e => e.id === entryId);
    if (entry) setDepositEntry(entry);
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">TDS / TCS Management</h1>
            <p className="text-sm text-slate-400 mt-0.5">Tax deduction and collection tracking</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowQuarterlyReturn(true)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-bar-chart-line" /> Quarterly Return
            </button>
            <button
              onClick={() => setShowGenerateChallan(true)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-file-paper-line" /> Generate Challan
            </button>
            <button
              onClick={() => navigate('/compliance/tds/settings')}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-slate-500 hover:bg-[#f8fafc] cursor-pointer transition-colors"
              title="TDS Settings"
            >
              <i className="ri-settings-3-line" />
            </button>
          </div>
        </div>

        {/* Summary Cards (hidden on dashboard/receivable tabs since each has its own) */}
        {activeTab !== 'dashboard' && activeTab !== 'receivable' && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'TDS Deducted (FY Total)', value: tdsThisMonth, color: 'text-blue-600', bg: 'bg-blue-50', icon: 'ri-download-line' },
              { label: 'TCS Collected (FY Total)', value: tcsThisMonth, color: 'text-teal-600', bg: 'bg-teal-50', icon: 'ri-upload-line' },
              { label: 'Pending Deposit', value: pendingDeposit, color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line' },
              { label: 'Deposited (This Quarter)', value: depositedQtr, color: 'text-green-600', bg: 'bg-green-50', icon: 'ri-check-double-line' },
            ].map(card => (
              <div key={card.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bg}`}>
                  <i className={`${card.icon} ${card.color}`} />
                </div>
                <div>
                  <p className="text-xs text-slate-400">{card.label}</p>
                  <p className={`text-lg font-bold ${card.color}`}>{formatINR(card.value)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4">
          {[
            { key: 'dashboard' as const, label: 'Dashboard', count: null },
            { key: 'tds' as const, label: 'TDS Entries', count: entriesSource.filter(e => e.type === 'TDS').length },
            { key: 'tcs' as const, label: 'TCS Entries', count: entriesSource.filter(e => e.type === 'TCS').length },
            { key: 'challan' as const, label: 'Challan Register', count: challanGroupsList.length },
            { key: 'receivable' as const, label: 'TDS Receivable', count: receivableCount },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.key
                  ? 'bg-[#4f46e5] text-white'
                  : 'bg-white text-slate-600 border border-[#e2e8f0] hover:border-[#4f46e5]'
                }`}
            >
              {tab.label}
              {tab.count != null && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${activeTab === tab.key ? 'bg-white/20' : 'bg-[#f1f5f9]'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <DashboardTab entries={tdsEntriesList} sections={tdsSectionsList} onDepositNow={handleDepositFromDashboard} onViewReceivable={() => setActiveTab('receivable')} />
        )}

        {/* TDS Receivable Tab (Form 16A certificate tracking) */}
        {activeTab === 'receivable' && <TdsReceivableTab onReconcile={() => setShowQuarterlyReturn(true)} />}

        {/* Filters (TDS/TCS tabs only) */}
        {(activeTab === 'tds' || activeTab === 'tcs') && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search party, PAN, invoice..."
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]" />
            </div>
            <select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white text-slate-600 focus:outline-none focus:border-[#4f46e5]">
              <option value="">All Sections</option>
              {usedSections.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white text-slate-600 focus:outline-none focus:border-[#4f46e5]">
              <option value="">All Status</option>
              <option value="DEDUCTED">Deducted</option><option value="DEPOSITED">Deposited</option>
              <option value="PENDING">Pending</option><option value="EXEMPTED">Exempted</option>
            </select>
            <button onClick={() => { setSearch(''); setSectionFilter(''); setStatusFilter(''); }}
              className="text-xs text-[#4f46e5] font-medium cursor-pointer hover:underline whitespace-nowrap">Reset Filters</button>
          </div>
        )}

        {/* TDS / TCS Table */}
        {(activeTab === 'tds' || activeTab === 'tcs') && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="py-3 px-4 font-medium">Date</th><th className="py-3 px-4 font-medium">Party</th>
                  <th className="py-3 px-4 font-medium">PAN</th><th className="py-3 px-4 font-medium">Section</th>
                  <th className="py-3 px-4 font-medium text-right">Payment Amt</th><th className="py-3 px-4 font-medium text-right">Taxable Amt</th>
                  <th className="py-3 px-4 font-medium text-right">Rate%</th><th className="py-3 px-4 font-medium text-right">TDS Amount</th>
                  <th className="py-3 px-4 font-medium">Status</th><th className="py-3 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allEntries.length === 0 ? (
                  <tr><td colSpan={10} className="py-12 text-center text-slate-400">
                    <i className="ri-inbox-line text-3xl block mb-2" /><p className="text-sm">No entries found</p>
                  </td></tr>
                ) : (
                  allEntries.map(e => (
                    <tr key={e.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">
                      <td className="py-3 px-4 text-[#1e293b]">{formatDate(e.paymentDate)}</td>
                      <td className="py-3 px-4"><p className="font-medium text-[#1e293b]">{e.partyName}</p></td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">{e.partyPAN}</td>
                      <td className="py-3 px-4"><span className="text-xs font-medium text-slate-600">{e.section}</span></td>
                      <td className="py-3 px-4 text-right font-medium text-[#1e293b]">{formatINR(e.paymentAmount)}</td>
                      <td className="py-3 px-4 text-right text-[#1e293b]">{formatINR(e.taxableAmount)}</td>
                      <td className="py-3 px-4 text-right font-medium">{e.tdsRate}%</td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">{formatINR(e.tdsAmount)}</td>
                      <td className="py-3 px-4">{statusBadge(e.status)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setDetailEntry(e)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-[#e2e8f0] text-slate-500 cursor-pointer transition-colors" title="View Details">
                            <i className="ri-eye-line text-sm" />
                          </button>
                          {e.status === 'DEDUCTED' && (
                            <button onClick={() => setDepositEntry(e)}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-green-50 text-green-600 cursor-pointer transition-colors" title="Deposit TDS">
                              <i className="ri-bank-line text-sm" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Challan Register Tab */}
        {activeTab === 'challan' && (
          <div className="space-y-4">
            {/* TDS / TCS toggle — same ITNS 281 form, different section (194-series vs 206C) */}
            <div className="flex items-center gap-2">
              {(['TDS', 'TCS'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setChallanTypeView(t)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer whitespace-nowrap transition-colors ${challanTypeView === t ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-[#4f46e5]'}`}
                >
                  {t === 'TDS' ? 'TDS (Vendor Deduction)' : 'TCS (Buyer Collection)'}
                </button>
              ))}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50"><i className="ri-wallet-3-line text-[#4f46e5]" /></div>
                <div>
                  <p className="text-xs text-slate-400">Total {challanTypeView} (All Pending Months)</p>
                  <p className="text-lg font-bold text-[#4f46e5]">{formatINR((challanSummary?.totalDeposited || 0) + (challanSummary?.totalPending || 0))}</p>
                </div>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-50"><i className="ri-checkbox-circle-line text-emerald-600" /></div>
                <div>
                  <p className="text-xs text-slate-400">Deposited</p>
                  <p className="text-lg font-bold text-emerald-600">{formatINR(challanSummary?.totalDeposited || 0)}</p>
                </div>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-50"><i className="ri-time-line text-amber-600" /></div>
                <div>
                  <p className="text-xs text-slate-400">Pending</p>
                  <p className="text-lg font-bold text-amber-600">{formatINR(challanSummary?.totalPending || 0)}</p>
                </div>
              </div>
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100"><i className="ri-calendar-event-line text-slate-500" /></div>
                <div>
                  <p className="text-xs text-slate-400">Next Due</p>
                  <p className="text-sm font-bold text-[#1e293b]">
                    {challanSummary?.nextDue ? formatDate(challanSummary.nextDue.date) : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Overdue banner */}
            {challanSummary?.nextDue && new Date() > new Date(challanSummary.nextDue.date) && (
              <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 border bg-red-50 border-red-200">
                <i className="ri-alarm-warning-line text-red-600" />
                <p className="text-sm font-medium text-red-700">
                  {formatDate(new Date(challanSummary.nextDue.year, challanSummary.nextDue.month - 1, 1))} {challanTypeView} of {formatINR(challanSummary.nextDue.amount)} overdue!
                  Due: {formatDate(challanSummary.nextDue.date)}. <button onClick={() => setShowGenerateChallan(true)} className="underline font-semibold cursor-pointer">Deposit Now</button>
                </p>
              </div>
            )}

            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="py-3 px-4 font-medium">Challan Number</th><th className="py-3 px-4 font-medium">BSR Code</th>
                  <th className="py-3 px-4 font-medium">Date</th><th className="py-3 px-4 font-medium">Bank</th>
                  <th className="py-3 px-4 font-medium text-center">Entries</th><th className="py-3 px-4 font-medium text-right">Total Amount</th>
                  <th className="py-3 px-4 font-medium">Quarter</th><th className="py-3 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {challanGroups.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400">
                    <i className="ri-inbox-line text-3xl block mb-2" /><p className="text-sm">No challan records found</p>
                  </td></tr>
                ) : (
                  challanGroups.map(g => (
                    <tr key={g.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">
                      <td className="py-3 px-4 font-mono font-medium text-[#4f46e5]">{g.challanNumber}</td>
                      <td className="py-3 px-4 text-slate-500">{g.bsrCode}</td>
                      <td className="py-3 px-4 text-[#1e293b]">{formatDate(g.depositDate)}</td>
                      <td className="py-3 px-4 text-[#1e293b]">{g.bankName}</td>
                      <td className="py-3 px-4 text-center font-medium">{g.entryIds.length}</td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">{formatINR(g.totalAmount)}</td>
                      <td className="py-3 px-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{g.quarter} {g.financialYear}</span></td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <button onClick={() => setChallanDetail(g)}
                            className="px-3 py-1.5 rounded-md text-xs font-medium text-[#4f46e5] hover:bg-indigo-50 cursor-pointer whitespace-nowrap transition-colors">
                            <i className="ri-eye-line mr-1" /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Slide-Over */}
      {detailEntry && <DetailSlideOver entry={detailEntry} sections={tdsSectionsList} onClose={() => setDetailEntry(null)} onForm16A={setForm16AEntry} />}

      {/* Deposit Modal */}
      {depositEntry && <DepositModal entry={depositEntry} onClose={() => setDepositEntry(null)} onSaved={() => { setDepositEntry(null); fetchTDSData(); }} />}

      {/* Generate Challan Details → Mark as Deposited (real interest calc, ITNS 281 preview) */}
      {showGenerateChallan && <ChallanDepositFlow type={challanTypeView} onClose={() => setShowGenerateChallan(false)} onGenerated={() => { setShowGenerateChallan(false); fetchTDSData(); }} />}

      {/* Challan Detail Modal */}
      {challanDetail && <ChallanDetailModal group={challanDetail} onClose={() => setChallanDetail(null)} />}

      {/* Quarterly Return Full Page */}
      {showQuarterlyReturn && <QuarterlyReturnPage entries={entriesSource} onClose={() => setShowQuarterlyReturn(false)} />}

      {/* Form 16A Certificate */}
      {form16AEntry && <Form16ACertificate entry={form16AEntry} onClose={() => setForm16AEntry(null)} />}
    </AppLayout>
  );
}